import React from 'react';
import { Link } from 'react-router-dom';
import Logo from '../../components/Logo';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="min-vh-100 bg-light">
      {/* Navigation */}
      <nav className="navbar navbar-expand-lg navbar-light bg-white border-bottom">
        <div className="container">
          <Link className="navbar-brand d-flex align-items-center" to="/">
            <Logo size="sm" className="me-2" />
            <span className="fw-bold">Dream Digi</span>
          </Link>
          
          <div className="navbar-nav ms-auto">
            <Link className="nav-link" to="/login">Login</Link>
            <Link className="nav-link btn btn-primary ms-2" to="/register">Get Started</Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-lg-8">
            <div className="card border-0 shadow-sm">
              <div className="card-body p-5">
                <div className="text-center mb-5">
                  <Logo size="lg" className="mb-3" />
                  <h1 className="h2 fw-bold">Privacy Policy</h1>
                  <p className="text-muted">Last updated: August 10, 2025</p>
                </div>

                <div className="legal-content">
                  <section className="mb-4">
                    <h2 className="h4 fw-bold mb-3">1. Introduction</h2>
                    <p>
                      Dream Digi ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our AI-powered website optimization service.
                    </p>
                  </section>

                  <section className="mb-4">
                    <h2 className="h4 fw-bold mb-3">2. Information We Collect</h2>
                    
                    <h5 className="fw-bold mb-2">2.1 Personal Information</h5>
                    <p>We may collect the following personal information:</p>
                    <ul>
                      <li>Name and contact information (email, phone number)</li>
                      <li>Company name and website URL</li>
                      <li>Account credentials and preferences</li>
                      <li>Payment information (processed securely by third-party providers)</li>
                    </ul>

                    <h5 className="fw-bold mb-2 mt-3">2.2 Website Data</h5>
                    <p>When you use our service, we may collect:</p>
                    <ul>
                      <li>Website content and structure data</li>
                      <li>Performance metrics and analytics</li>
                      <li>SEO and optimization recommendations</li>
                      <li>Usage patterns and preferences</li>
                    </ul>

                    <h5 className="fw-bold mb-2 mt-3">2.3 Technical Information</h5>
                    <p>We automatically collect:</p>
                    <ul>
                      <li>IP address and device information</li>
                      <li>Browser type and version</li>
                      <li>Operating system</li>
                      <li>Usage logs and analytics</li>
                    </ul>
                  </section>

                  <section className="mb-4">
                    <h2 className="h4 fw-bold mb-3">3. How We Use Your Information</h2>
                    <p>We use the collected information for:</p>
                    <ul>
                      <li>Providing and improving our services</li>
                      <li>Personalizing your experience</li>
                      <li>Processing payments and subscriptions</li>
                      <li>Sending important updates and notifications</li>
                      <li>Analyzing usage patterns and trends</li>
                      <li>Preventing fraud and ensuring security</li>
                      <li>Complying with legal obligations</li>
                    </ul>
                  </section>

                  <section className="mb-4">
                    <h2 className="h4 fw-bold mb-3">4. Information Sharing and Disclosure</h2>
                    <p>We do not sell, trade, or rent your personal information. We may share your information in the following circumstances:</p>
                    <ul>
                      <li><strong>Service Providers:</strong> With trusted third-party service providers who assist in operating our service</li>
                      <li><strong>Payment Processors:</strong> With secure payment gateways to process transactions</li>
                      <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
                      <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
                      <li><strong>Consent:</strong> With your explicit consent for specific purposes</li>
                    </ul>
                  </section>

                  <section className="mb-4">
                    <h2 className="h4 fw-bold mb-3">5. Data Security</h2>
                    <p>We implement appropriate security measures to protect your information:</p>
                    <ul>
                      <li>Encryption of data in transit and at rest</li>
                      <li>Secure authentication and access controls</li>
                      <li>Regular security audits and updates</li>
                      <li>Employee training on data protection</li>
                      <li>Incident response procedures</li>
                    </ul>
                  </section>

                  <section className="mb-4">
                    <h2 className="h4 fw-bold mb-3">6. Data Retention</h2>
                    <p>
                      We retain your personal information for as long as necessary to provide our services and comply with legal obligations. You may request deletion of your data, subject to certain legal requirements.
                    </p>
                  </section>

                  <section className="mb-4">
                    <h2 className="h4 fw-bold mb-3">7. Your Rights</h2>
                    <p>You have the following rights regarding your personal information:</p>
                    <ul>
                      <li><strong>Access:</strong> Request a copy of your personal data</li>
                      <li><strong>Correction:</strong> Update or correct inaccurate information</li>
                      <li><strong>Deletion:</strong> Request deletion of your personal data</li>
                      <li><strong>Portability:</strong> Request transfer of your data</li>
                      <li><strong>Objection:</strong> Object to certain processing activities</li>
                      <li><strong>Withdrawal:</strong> Withdraw consent where applicable</li>
                    </ul>
                  </section>

                  <section className="mb-4">
                    <h2 className="h4 fw-bold mb-3">8. Cookies and Tracking</h2>
                    <p>
                      We use cookies and similar technologies to enhance your experience, analyze usage, and provide personalized content. You can control cookie settings through your browser preferences.
                    </p>
                  </section>

                  <section className="mb-4">
                    <h2 className="h4 fw-bold mb-3">9. Third-Party Services</h2>
                    <p>
                      Our service may integrate with third-party services. These services have their own privacy policies, and we encourage you to review them. We are not responsible for the privacy practices of third-party services.
                    </p>
                  </section>

                  <section className="mb-4">
                    <h2 className="h4 fw-bold mb-3">10. Children's Privacy</h2>
                    <p>
                      Our service is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you believe we have collected such information, please contact us immediately.
                    </p>
                  </section>

                  <section className="mb-4">
                    <h2 className="h4 fw-bold mb-3">11. International Data Transfers</h2>
                    <p>
                      Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place to protect your data in accordance with this Privacy Policy.
                    </p>
                  </section>

                  <section className="mb-4">
                    <h2 className="h4 fw-bold mb-3">12. Changes to This Policy</h2>
                    <p>
                      We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on our website and updating the "Last updated" date.
                    </p>
                  </section>

                  <section className="mb-4">
                    <h2 className="h4 fw-bold mb-3">13. Contact Us</h2>
                    <p>
                      If you have any questions about this Privacy Policy or our data practices, please contact us at:
                    </p>
                    <ul>
                      <li>Email: privacy@dream-digi.com</li>
                      <li>Website: ai.dream-digi.com</li>
                      <li>Address: [Your Business Address]</li>
                    </ul>
                  </section>
                </div>

                <div className="text-center mt-5">
                  <Link to="/register" className="btn btn-primary me-3">
                    Get Started
                  </Link>
                  <Link to="/" className="btn btn-outline-secondary">
                    Back to Home
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
